import React, { useEffect, useState } from 'react';
import {
  Modal,
  Tabs,
  Tab,
  Table,
  Form,
  Button,
  Spinner,
  Accordion,
  Card,
  Row,
  Col,
  Alert,
  ProgressBar,
  InputGroup,
  Badge,
  ButtonGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import {
  FaShieldAlt,
  FaSearch,
  FaCheck,
  FaTimes,
  FaEye,
  FaEdit,
  FaUsers,
  FaCog,
  FaSave,
  FaUndo,
  FaCheckSquare,
  FaBolt
} from 'react-icons/fa';
import {
  BsShield,
  BsSearch,
  BsInfoCircle,
  BsCheck2All,
  BsX,
  BsLightning,
  BsGear,
  BsFilter,
  BsFileText
} from 'react-icons/bs';
import { MdSecurity, MdSettings, MdSelectAll, MdWarning } from 'react-icons/md';

import rbacService from '../../../services/rbacService';
import dynamicFormsService from '../../../services/dynamicFormsService';

/**
 * ConfigurePermissionsModal
 * 
 * This modal is designed ONLY for role-based permission configuration.
 * It should NOT be used for direct user permission assignment.
 * Users get permissions through role assignments.
 * 
 * Props:
 *  - show (boolean)
 *  - roleId (string) - The role ID to configure permissions for
 *  - onHide () => void
 *  - onSaved () => void   // callback after successful save
 */
const ConfigurePermissionsModal = ({ show, roleId, onHide, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [globalPermissions, setGlobalPermissions] = useState({}); // { category: Permission[] }
  const [formList, setFormList] = useState([]); // summary list of forms
  const [grants, setGrants] = useState(new Set()); // Set of granted permission codes
  const [originalGrants, setOriginalGrants] = useState(new Set()); // Original state for change detection
  const [codeToIdMap, setCodeToIdMap] = useState({}); // Map of code -> UUID for saving
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  // const [viewMode, setViewMode] = useState('simplified'); // simplified, detailed - unused
  const [hasChanges, setHasChanges] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [activeTab, setActiveTab] = useState('system');
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [stats, setStats] = useState({
    totalPermissions: 0,
    grantedPermissions: 0,
    formPermissions: 0,
    globalPermissions: 0
  });

  // Enhanced helper functions
  const isGranted = (pid) => {
    return grants.has(pid);
  };

  const toggleGrant = (pid) => {
    if (!pid || pid === 'undefined' || pid === 'null') {
      console.warn('Invalid permission ID:', pid);
      return;
    }

    const permissionId = String(pid);
    console.log('Toggling permission:', permissionId);

    setGrants((prev) => {
      const next = new Set(prev);
      const wasGranted = next.has(permissionId);

      if (wasGranted) {
        next.delete(permissionId);
        console.log('Removed permission:', permissionId);
      } else {
        next.add(permissionId);
        console.log('Added permission:', permissionId);
      }

      console.log('Current grants after toggle:', Array.from(next));
      return next;
    });
  };

  const detectChanges = () => {
    // Check if sets are different by comparing both directions
    const changed = grants.size !== originalGrants.size ||
      ![...grants].every(pid => originalGrants.has(pid)) ||
      ![...originalGrants].every(pid => grants.has(pid));
    
    console.log('Detecting changes:', {
      grantsSize: grants.size,
      originalGrantsSize: originalGrants.size,
      currentGrants: Array.from(grants),
      originalGrants: Array.from(originalGrants),
      hasChanges: changed
    });
    
    setHasChanges(changed);
  };

  const calculateStats = () => {
    const totalForms = formList.length;
    const systemPermsCount = Object.values(globalPermissions).flat().length;
    const totalFormPerms = totalForms * 2; // 2 perms per form (view, edit)
    const totalPerms = totalFormPerms + systemPermsCount;

    // Count granted system permissions
    const grantedSystemPerms = [...grants].filter(pid =>
      pid && !pid.startsWith('forms.')
    ).length;

    // Count granted form permissions 
    const grantedFormPerms = [...grants].filter(pid =>
      pid && pid.startsWith('forms.')
    ).length;

    setStats({
      totalPermissions: totalPerms,
      grantedPermissions: grants.size,
      formPermissions: totalForms,
      globalPermissions: systemPermsCount,
      grantedSystemPermissions: grantedSystemPerms,
      grantedFormPermissions: grantedFormPerms
    });
  };

  // Permission template operations
  const applyTemplate = (templateType) => {
    let newGrants = new Set();

    switch (templateType) {
      case 'full_access':
        // Grant all system permissions
        Object.values(globalPermissions).flat().forEach(perm => {
          newGrants.add(perm.permission_id); // permission_id is now normalized to use code
        });
        // Grant all form permissions (view and edit)
        formList.forEach(form => {
          newGrants.add(`forms.${form._id || form.form_id}.view`);
          newGrants.add(`forms.${form._id || form.form_id}.edit`);
        });
        break;
      case 'read_only':
        // Grant only view permissions for forms, no system permissions
        formList.forEach(form => {
          newGrants.add(`forms.${form._id || form.form_id}.view`);
        });
        break;
      case 'no_access':
        // Remove all permissions
        newGrants = new Set();
        break;
      default:
        break;
    }

    setGrants(newGrants);
    detectChanges();
  };

  const getPermissionsByCategory = () => {
    if (selectedCategory === 'all') {
      return Object.entries(globalPermissions);
    }
    return Object.entries(globalPermissions).filter(([cat]) => cat === selectedCategory);
  };

  const getFilteredForms = () => {
    return formList.filter(form =>
      form.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (form.department_name || form.departmentName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Fetch data on open
  useEffect(() => {
    if (!show || !roleId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. fetch ALL permissions FIRST to build UUID -> code mapping
        let allPerms = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await rbacService.getPermissions({ page, page_size: 1000 });
          allPerms = allPerms.concat(res.data || []);
          hasMore = res.hasMore || false;
          page = (res.page || page) + 1;
        }

        // Build bidirectional maps for UUID <-> code conversion
        const idToCodeMap = {};
        const codeToIdMapping = {};
        allPerms.forEach((perm) => {
          const permId = perm.id; // UUID
          const permCode = perm.code || perm.permission_id; // Human-readable code
          if (permId && permCode) {
            idToCodeMap[permId] = permCode;
            codeToIdMapping[permCode] = permId;
          }
        });
        setCodeToIdMap(codeToIdMapping);
        console.log('📋 UUID to Code mapping sample:', Object.entries(idToCodeMap).slice(0, 5));
        console.log('📋 Code to UUID mapping sample:', Object.entries(codeToIdMapping).slice(0, 5));

        // 2. fetch role (to get current permissions and role name)
        const roleRes = await rbacService.getRoleById(roleId);
        const role = roleRes?.data || roleRes;
        console.log('📋 Role permissions response:', role?.permissions);
        
        // Convert role permissions from UUIDs to codes
        const currentPerms = new Set((role?.permissions || []).map((p) => {
          const permUUID = p.permission_id || p.id;
          const permCode = idToCodeMap[permUUID] || permUUID; // Convert UUID to code, or use as-is if not found
          console.log('📋 Mapping permission UUID:', permUUID, '-> Code:', permCode);
          return permCode;
        }));
        console.log('📋 Current permissions set (as codes):', Array.from(currentPerms));
        setGrants(currentPerms);
        setOriginalGrants(new Set(currentPerms));
        setRoleName(role?.name || role?.display_name || 'Unknown Role');

        // Separate system permissions (non-form permissions)
        // Form permissions have permission_id starting with 'forms.' regardless of their category
        console.log('📋 Total permissions fetched:', allPerms.length);
        console.log('📋 Sample permission structure:', allPerms[0]);
        
        const globalMap = {};
        allPerms.forEach((perm) => {
          // Skip form-specific permissions - they'll be handled in form tab
          // Forms permissions always have permission_id starting with 'forms.' pattern
          // Use code as the primary identifier (human-readable), fallback to id (UUID)
          const permCode = perm.code || perm.permission_id || perm.id || '';
          
          if (/^forms\./i.test(permCode)) {
            // This is a form permission, skip it in system tab
            console.log('✂️ Skipping form permission:', permCode, 'category:', perm.category);
            return;
          }
          
          // Normalize permission object to use code as permission_id
          const normalizedPerm = {
            ...perm,
            permission_id: permCode // Use code as the permission_id for consistency
          };
          
          // This is a system permission, add to appropriate category
          const cat = perm.category || perm.module || 'general';
          if (!globalMap[cat]) globalMap[cat] = [];
          globalMap[cat].push(normalizedPerm);
          console.log('✅ Added system permission to category', cat, ':', permCode);
        });
        
        console.log('📊 System permissions by category:', Object.keys(globalMap).map(cat => `${cat}: ${globalMap[cat].length}`));
        setGlobalPermissions(globalMap);
        setPermissionCategories(Object.keys(globalMap));

        // 3. fetch form summary list
        const formRes = await dynamicFormsService.getForms({ summary: true, page_size: 500 });
        setFormList(formRes.data || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [show, roleId]);

  // Calculate stats whenever grants or form list changes
  useEffect(() => {
    if (formList.length > 0 || Object.keys(globalPermissions).length > 0) {
      calculateStats();
    }
  }, [grants, formList, globalPermissions]);

  // Detect changes whenever grants or originalGrants change
  useEffect(() => {
    detectChanges();
  }, [grants, originalGrants]);

  const handleSave = async () => {
    setSaving(true);
    setSaveProgress(0);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setSaveProgress(prev => {
        if (prev < 90) return prev + 10;
        return prev;
      });
    }, 200);

    try {
      // Calculate all permission codes that have been affected (granted or revoked)
      const allAffectedPermissions = new Set([...grants, ...originalGrants]);
      
      // Convert codes back to UUIDs and create payload with proper granted/revoked status
      const payload = Array.from(allAffectedPermissions)
        .filter(code => code && code !== 'undefined' && code !== 'null')
        .map((code) => {
          // Convert code to UUID for API
          const uuid = codeToIdMap[code] || code;
          return {
            permission_id: String(uuid),
            granted: grants.has(code) // true if in current grants, false if removed
          };
        });

      console.log('💾 Saving permissions:', { 
        roleId, 
        codesBeingProcessed: Array.from(allAffectedPermissions),
        payloadWithUUIDs: payload, 
        currentGrantedCodes: Array.from(grants),
        originalGrantedCodes: Array.from(originalGrants),
        grantedUUIDs: payload.filter(p => p.granted).map(p => p.permission_id),
        revokedUUIDs: payload.filter(p => !p.granted).map(p => p.permission_id)
      });

      if (payload.length === 0) {
        throw new Error('No permissions to process');
      }

      await rbacService.assignPermissionsToRole(roleId, payload);

      clearInterval(progressInterval);
      setSaveProgress(100);

      toast.success(
        <div>
          <strong>Success!</strong><br />
          Permissions updated for {roleName}
        </div>
      );

      // Reset change state
      setOriginalGrants(new Set(grants));
      setHasChanges(false);

      setTimeout(() => {
        onSaved && onSaved();
        onHide();
      }, 1500);
    } catch (err) {
      clearInterval(progressInterval);
      setSaveProgress(0);
      console.error(err);

      // Handle RBAC/Permission errors (403) explicitly
      const status = err?.response?.status;
      if (status === 403) {
        toast.error(err?.response?.data?.detail || 'You do not have permission to configure permissions.');
        return;
      }

      toast.error(
        <div>
          <strong>Error!</strong><br />
          Failed to update permissions: {err.message || 'Unknown error'}
        </div>
      );
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-xl">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaShieldAlt className="text-primary" />
          Configure Permissions - {roleName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading permissions...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Permission Overview and Quick Actions */}
            <Row className="mb-4">
              <Col md={8}>
                {/* Statistics Cards */}
                <Row>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body className="py-3">
                        <h5 className="mb-1 text-primary">{stats.grantedPermissions}</h5>
                        <small className="text-muted">Granted</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body className="py-3">
                        <h5 className="mb-1 text-info">{stats.totalPermissions}</h5>
                        <small className="text-muted">Total</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body className="py-3">
                        <h5 className="mb-1 text-success">{stats.grantedFormPermissions || 0}</h5>
                        <small className="text-muted">Form Perms</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center border-0 bg-light">
                      <Card.Body className="py-3">
                        <h5 className="mb-1 text-warning">{stats.grantedSystemPermissions || 0}</h5>
                        <small className="text-muted">System Perms</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
              <Col md={4}>
                {/* Quick Actions */}
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <h6 className="mb-3">
                      <BsLightning className="me-2" />
                      Quick Actions
                    </h6>
                    <ButtonGroup size="sm" className="w-100">
                      <OverlayTrigger overlay={<Tooltip>Grant all permissions</Tooltip>}>
                        <Button variant="outline-success" onClick={() => applyTemplate('full_access')}>
                          <FaCheckSquare className="me-1" />
                          All
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger overlay={<Tooltip>Grant view-only access</Tooltip>}>
                        <Button variant="outline-warning" onClick={() => applyTemplate('read_only')}>
                          <FaEye className="me-1" />
                          View
                        </Button>
                      </OverlayTrigger>
                      <OverlayTrigger overlay={<Tooltip>Remove all permissions</Tooltip>}>
                        <Button variant="outline-danger" onClick={() => applyTemplate('no_access')}>
                          <FaTimes className="me-1" />
                          None
                        </Button>
                      </OverlayTrigger>
                    </ButtonGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Change Detection Alert */}
            {hasChanges && (
              <Alert variant="warning" className="d-flex align-items-center mb-4">
                <MdWarning className="me-2" />
                <div>
                  <strong>Unsaved Changes</strong>
                  <br />
                  <small>You have made changes to the permissions. Don't forget to save!</small>
                </div>
              </Alert>
            )}

            {/* Main Permission Tabs */}
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
              <Tab eventKey="system" title={<><FaCog className="me-2" />System Permissions</>}>
                <div className="pt-3">
                  {/* Category Filter */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <BsFilter />
                        </InputGroup.Text>
                        <Form.Select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          <option value="all">All Categories</option>
                          {permissionCategories.map(cat => (
                            <option key={cat} value={cat}>
                              {cat.replace(/_/g, ' ').toUpperCase()}
                            </option>
                          ))}
                        </Form.Select>
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <BsSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search permissions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                  </Row>

                  <Accordion alwaysOpen>
                    {getPermissionsByCategory().map(([cat, perms]) => {
                      const filteredPerms = perms.filter(perm =>
                        perm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        perm.description?.toLowerCase().includes(searchTerm.toLowerCase())
                      );

                      if (filteredPerms.length === 0) return null;
                      console.log("filteredPerms", filteredPerms)
                      const grantedCount = filteredPerms.filter(p => {
                        const permId = p.permission_id || p.id;
                        return grants.has(permId);
                      }).length;
                      console.log("grantedCount", grantedCount)
                      return (
                        <Accordion.Item eventKey={cat} key={cat}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span>{cat.replace(/_/g, ' ').toUpperCase()}</span>
                              <Badge bg={grantedCount === 0 ? 'light' : grantedCount === filteredPerms.length ? 'success' : 'warning'}>
                                {grantedCount}/{filteredPerms.length}
                              </Badge>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Table hover responsive size="sm">
                              <thead className="table-light">
                                <tr>
                                  <th>Permission</th>
                                  <th>Description</th>
                                  <th className="text-center">
                                    <FaEye className="me-1" />
                                    Access
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredPerms.map((perm, index) => {
                                  const permissionId = perm.permission_id; // Already normalized to use code
                                  const switchId = `permission-switch-${permissionId || index}`;
                                  const isChecked = isGranted(permissionId);

                                  return (
                                    <tr key={permissionId || `perm-${index}`}>
                                      <td>
                                        <strong>{perm.name || perm.display_name}</strong>
                                        <br />
                                        <small className="text-muted">{permissionId}</small>
                                      </td>
                                      <td>
                                        <small className="text-muted">{perm.description || 'No description available'}</small>
                                      </td>
                                      <td className="text-center">
                                        <Form.Check
                                          type="switch"
                                          id={switchId}
                                          checked={isChecked}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleGrant(permissionId);
                                          }}
                                          disabled={saving}
                                          size="lg"
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                </div>
              </Tab>

              <Tab eventKey="forms" title={<><BsFileText className="me-2" />Form Permissions</>}>
                <div className="pt-3">
                  {/* Info Alert */}
                  <Alert variant="info" className="mb-3">
                    <BsInfoCircle className="me-2" />
                    <strong>Form Permissions:</strong> Control access and editing rights for each form. Access allows viewing form data, while Edit allows modifying submissions.
                  </Alert>

                  {/* Search and Bulk Actions */}
                  <Row className="mb-3">
                    <Col md={8}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <BsSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search forms by name or department..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <ButtonGroup size="sm" className="w-100">
                        <OverlayTrigger overlay={<Tooltip>Grant access and edit for all visible forms</Tooltip>}>
                          <Button
                            variant="outline-success"
                            onClick={() => {
                              getFilteredForms().forEach(form => {
                                const viewPid = `forms.${form._id || form.form_id}.view`;
                                const editPid = `forms.${form._id || form.form_id}.edit`;
                                setGrants(prev => new Set([...prev, viewPid, editPid]));
                              });
                              detectChanges();
                            }}
                          >
                            <FaCheckSquare className="me-1" />
                            All
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger overlay={<Tooltip>Grant only access for all visible forms</Tooltip>}>
                          <Button
                            variant="outline-warning"
                            onClick={() => {
                              getFilteredForms().forEach(form => {
                                const viewPid = `forms.${form._id || form.form_id}.view`;
                                const editPid = `forms.${form._id || form.form_id}.edit`;
                                setGrants(prev => {
                                  const newSet = new Set([...prev, viewPid]);
                                  newSet.delete(editPid);
                                  return newSet;
                                });
                              });
                              detectChanges();
                            }}
                          >
                            <FaEye className="me-1" />
                            View
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger overlay={<Tooltip>Remove all permissions for visible forms</Tooltip>}>
                          <Button
                            variant="outline-danger"
                            onClick={() => {
                              getFilteredForms().forEach(form => {
                                const viewPid = `forms.${form._id || form.form_id}.view`;
                                const editPid = `forms.${form._id || form.form_id}.edit`;
                                setGrants(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(viewPid);
                                  newSet.delete(editPid);
                                  return newSet;
                                });
                              });
                              detectChanges();
                            }}
                          >
                            <FaTimes className="me-1" />
                            None
                          </Button>
                        </OverlayTrigger>
                      </ButtonGroup>
                    </Col>
                  </Row>

                  <Table hover responsive size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>Form Name</th>
                        <th>Department</th>
                        <th className="text-center">
                          <OverlayTrigger overlay={<Tooltip>View and access form data</Tooltip>}>
                            <span>
                              <FaEye className="me-1" />
                              Access
                            </span>
                          </OverlayTrigger>
                        </th>
                        <th className="text-center">
                          <OverlayTrigger overlay={<Tooltip>Modify and update form data</Tooltip>}>
                            <span>
                              <FaEdit className="me-1" />
                              Edit
                            </span>
                          </OverlayTrigger>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredForms().map((form, index) => {
                        const formId = form._id || form.form_id;
                        const viewPid = `forms.${formId}.view`;
                        const editPid = `forms.${formId}.edit`;
                        const hasAccess = isGranted(viewPid);
                        const hasEdit = isGranted(editPid);
                        const viewSwitchId = `form-view-${formId || index}`;
                        const editSwitchId = `form-edit-${formId || index}`;

                        return (
                          <tr key={formId || `form-${index}`} className={hasAccess || hasEdit ? 'table-success' : ''}>
                            <td>
                              <strong>{form.name}</strong>
                              {(hasAccess || hasEdit) && (
                                <div className="mt-1">
                                  {hasAccess && <Badge bg="info" size="sm" className="me-1">Access</Badge>}
                                  {hasEdit && <Badge bg="warning" size="sm">Edit</Badge>}
                                </div>
                              )}
                            </td>
                            <td>
                              <small className="text-muted">{form.department_name || form.departmentName || 'Unknown'}</small>
                            </td>
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                id={viewSwitchId}
                                checked={hasAccess}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleGrant(viewPid);
                                }}
                                disabled={saving}
                                size="lg"
                              />
                            </td>
                            <td className="text-center">
                              <Form.Check
                                type="checkbox"
                                id={editSwitchId}
                                checked={hasEdit}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleGrant(editPid);
                                }}
                                disabled={saving}
                                size="lg"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {getFilteredForms().length === 0 && (
                    <Alert variant="info" className="text-center">
                      <BsInfoCircle className="me-2" />
                      No forms found matching your search criteria.
                    </Alert>
                  )}
                </div>
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {/* Progress Bar for Saving */}
        {saving && (
          <div className="w-100 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="text-muted">Saving permissions...</span>
              <span className="text-primary">{saveProgress}%</span>
            </div>
            <ProgressBar now={saveProgress} variant="primary" style={{ height: '6px' }} />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {hasChanges && (
              <Alert variant="warning" className="p-2 mb-0 d-flex align-items-center">
                <MdWarning className="me-2" />
                <small>You have unsaved changes</small>
              </Alert>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
              <BsX className="me-1" />
              Close
            </Button>
            {hasChanges && (
              <Button
                variant="outline-warning"
                onClick={() => {
                  setGrants(new Set(originalGrants));
                  setHasChanges(false);
                }}
                disabled={saving}
              >
                <FaUndo className="me-1" />
                Reset
              </Button>
            )}
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="d-flex align-items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigurePermissionsModal; 